/**
 * New-version detection for the SPA.
 *
 * The app is a Rancher pod that's replaced wholesale on deploy — content-hashed chunk
 * filenames change and the old ones vanish. A browser still running the previous build
 * then 404s on lazy chunks ("Failed to fetch dynamically imported module"). Rather than
 * silently reload (jarring; can clobber unsaved work), we surface an in-app banner and let
 * the user reload when ready.
 *
 * Two triggers feed this store:
 *  - proactive: poll `version.txt` (written into the image at build) and compare to the
 *    version loaded at startup → a soft, dismissable "update available" hint.
 *  - reactive: a `vite:preloadError` (a lazy chunk failed) → a REQUIRED prompt, because the
 *    view the user just navigated to can't render without reloading.
 */
type Listener = () => void;

let updateAvailable = false;
let required = false;
const listeners = new Set<Listener>();
let loadedVersion: string | null = null;

// BASE_URL is the app's served base (also the router basename) and ends in "/".
const VERSION_URL = `${import.meta.env.BASE_URL}version.txt`;

export function isUpdateAvailable(): boolean { return updateAvailable; }
export function isUpdateRequired(): boolean { return required; }

export function subscribeUpdate(listener: Listener): () => void {
  listeners.add(listener);
  return () => { listeners.delete(listener); };
}

/** Flag that a newer build exists. `required` (chunk load failed) can escalate a prior
 *  soft hint, but never de-escalates. Always re-notifies listeners on escalation. */
export function notifyUpdateAvailable(isRequired = false): void {
  if (isRequired) required = true;
  if (updateAvailable && !isRequired) return;
  updateAvailable = true;
  listeners.forEach((l) => l());
}

async function fetchVersion(): Promise<string | null> {
  try {
    const res = await fetch(`${VERSION_URL}?t=${Date.now()}`, { cache: "no-store" });
    if (!res.ok) return null;
    return (await res.text()).trim();
  } catch {
    return null;
  }
}

/** Capture the running version, then poll for changes on an interval and on tab focus. */
export async function initVersionCheck(intervalMs = 5 * 60 * 1000): Promise<void> {
  loadedVersion = await fetchVersion();
  if (!loadedVersion) return; // no version.txt → rely on the preloadError fallback only

  const check = async () => {
    if (updateAvailable) return;
    const v = await fetchVersion();
    if (v && v !== loadedVersion) notifyUpdateAvailable(false);
  };
  setInterval(check, intervalMs);
  window.addEventListener("focus", () => { void check(); });
}
